/* ------------------------------------------------------------------
   AudioPanel – desktop sidebar ➊ | mobile fly-out ➋
   • On mobile the fly-out now scales to 90 % of the viewport width
     and stays centred below the video canvas.
------------------------------------------------------------------- */
import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Form, Button, Dropdown, DropdownButton, Alert,
} from 'react-bootstrap';
import { X } from 'react-bootstrap-icons';
import useIsMobile from '../../../../../hooks/useIsMobile';

export default function AudioPanel ({
  videoRef,
  audio, setAudio,
  videoVolume, setVideoVolume,
  onOpenLibrary = () => {},
}) {
  const isMobile     = useIsMobile();
  const musicRef     = useRef(null);
  const fileInputRef = useRef(null);

  const [label, setLabel] = useState(null);
  const [error, setError] = useState(null);

  /* ───────── sync volumes ───────── */
  useEffect(() => {
    if (videoRef?.current) videoRef.current.volume = videoVolume;
  }, [videoRef, videoVolume]);

  useEffect(() => {
    if (musicRef.current && audio?.volume != null) {
      musicRef.current.volume = audio.volume;
    }
  }, [audio?.volume]);

  /* ───────── keep music in lock-step ───────── */
  useEffect(() => {
    const v = videoRef?.current;
    const a = musicRef.current;
    if (!v || !a) return;

    const align       = () => { a.currentTime = v.currentTime; };
    const handlePlay  = () => { align(); a.play().catch(() => {}); };
    const handlePause = () => a.pause();
    const handleEnd   = () => {
      v.currentTime = a.currentTime = 0;
      Promise.allSettled([v.play(), a.play()]);
    };

    v.addEventListener('seeked', align);
    v.addEventListener('play',   handlePlay);
    v.addEventListener('pause',  handlePause);
    v.addEventListener('ended',  handleEnd);

    return () => {
      v.removeEventListener('seeked', align);
      v.removeEventListener('play',   handlePlay);
      v.removeEventListener('pause',  handlePause);
      v.removeEventListener('ended',  handleEnd);
    };
  }, [videoRef, audio?.src]);

  /* ───────── autoplay first pick ───────── */
  useEffect(() => {
    const v = videoRef?.current;
    const a = musicRef.current;
    if (!v || !a || !audio?.src) return;
    a.currentTime = v.currentTime;
    Promise.allSettled([v.play(), a.play()]).catch(() => {});
  }, [audio?.src, videoRef]);

  /* ───────── handlers ───────── */
  const handleFile = e => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('audio/')) {
      setError('Unsupported file type. Please choose an audio file.');
      e.target.value = '';
      return;
    }
    setError(null);
    const url = URL.createObjectURL(file);
    setAudio({ src: url, file, volume: 1, title: file.name });
    setLabel(file.name);
  };

  const clearAudio     = () => { musicRef.current?.pause(); setAudio(null); setLabel(null); };
  const handleMusicVol = e => setAudio(p => ({ ...p, volume: +e.target.value }));
  const handleVidVol   = e => setVideoVolume(+e.target.value);

  /* derive nice label */
  useEffect(() => {
    if (audio?.file) return;
    if (audio?.title)     setLabel(audio.title);
    else if (audio?.src)  setLabel(decodeURIComponent(audio.src.split('/').pop().split('?')[0]));
    else setLabel(null);
  }, [audio]);

  /* ───────── layout tweaks ───────── */
  const wrapperStyle = isMobile
    ? {
        width: '90%',            // ← NEW: fill 90 % of viewport
        margin: '0 auto',        // centre under the video
        padding: 8,
        paddingTop: 4,
        maxHeight: '45vh',
        overflowY: 'auto',
      }
    : { padding: 16, width: 320 };

  /* ───────── UI ───────── */
  return (
    <div
      style={wrapperStyle}
      className={isMobile ? 'audio-mobile-flyout' : ''}
    >
      {audio?.src && <audio ref={musicRef} src={audio.src} preload="auto" />}

      <h5 className="mb-2">Background&nbsp;Audio</h5>

      {/* hidden native file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="audio/*"
        onChange={handleFile}
        className="d-none"
      />

      {/* picker – sticky on mobile */}
      <div style={isMobile ? { position:'sticky', top:0, background:'#fff', zIndex:2 } : {}}>
        <DropdownButton
          title="Select Audio"
          variant="outline-primary"
          align={isMobile ? 'center' : 'start'}
          className="mb-2 w-100"
          size="sm"
        >
          <Dropdown.Item onClick={onOpenLibrary}>
            Select from Library
          </Dropdown.Item>
          <Dropdown.Item onClick={() => fileInputRef.current.click()}>
            Upload File
          </Dropdown.Item>
        </DropdownButton>
      </div>

      {label && (
        <div className="d-flex align-items-center small text-muted mb-2">
          <span className="text-truncate me-2 flex-grow-1">{label}</span>
          <Button
            variant="link"
            size="sm"
            className="p-0"
            onClick={clearAudio}
            aria-label="Remove audio"
          >
            <X />
          </Button>
        </div>
      )}
      {error && (
        <Alert variant="danger" className="py-1">
          {error}
        </Alert>
      )}

      {/* volume sliders */}
      <Form.Group className="mt-3">
        <Form.Label className="mb-1">Video&nbsp;Volume</Form.Label>
        <Form.Range
          min={0} max={1} step={0.05}
          value={videoVolume}
          onChange={handleVidVol}
        />
      </Form.Group>

      {audio?.src && (
        <Form.Group className="mt-3">
          <Form.Label className="mb-1">Music&nbsp;Volume</Form.Label>
          <Form.Range
            min={0} max={1} step={0.05}
            value={audio.volume}
            onChange={handleMusicVol}
          />
        </Form.Group>
      )}
    </div>
  );
}

AudioPanel.propTypes = {
  videoRef       : PropTypes.object.isRequired,
  audio          : PropTypes.shape({
    src   : PropTypes.string,
    volume: PropTypes.number,
    title : PropTypes.string,
  }),
  setAudio       : PropTypes.func.isRequired,
  videoVolume    : PropTypes.number.isRequired,
  setVideoVolume : PropTypes.func.isRequired,
  onOpenLibrary  : PropTypes.func,
};
